import React, { useState, useRef, useEffect, TouchEvent } from 'react';
import { Stepper } from '../molecules/Stepper';
import { ArrowIconButton } from '../molecules/ArrowIconButton';
import { SimpleReactComponentProps } from '../../../types/components';

interface CarouselProps extends SimpleReactComponentProps {
	children: React.ReactElement | React.ReactElement[] | undefined;
	dataLength?: number;
	render?: boolean;
	className?: string;
	widthOfElement?: number;
}

const containerClasses = 'relative scroll-mt-[84px]';
const wrapperClasses = 'overflow-hidden no-scrollbar flex';
const itemClasses = 'flex justify-center min-w-full';
const arrowButtonClasses =
	'bg-[#FDB728] w-[40px] h-[40px] rounded-full text-white absolute ';
const arrowButtonLeftClasses =
	arrowButtonClasses +
	'left-3 top-1/2 transform -translate-y-16 -translate-x-1/2';
const arrowButtonRightClasses =
	arrowButtonClasses +
	'right-3 top-1/2 transform -translate-y-16 translate-x-1/2';
const arrowButtonIconClasses = 'w-[10px] h-[20px]';

export const Carousel = ({
	children,
	dataLength = 0,
	render = true,
	anchorId,
}: CarouselProps) => {
	const [currentStep, setCurrentStep] = useState(0);
	const stepperRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
	const direction = useRef<null | 'right' | 'left'>(null);
	const startX = useRef(0);
	const startY = useRef(0);
  const elementWidth =  containerRef?.current?.clientWidth || 0;

	const handleStepClick = (index: number) => {
		setCurrentStep(index);
	};

	const handleButtonClick = (direction: 'up' | 'down') => {
		if (direction === 'up') {
			currentStep + 1 < dataLength
				? handleStepClick(currentStep + 1)
				: handleStepClick(0);
		} else {
			currentStep - 1 >= 0
				? handleStepClick(currentStep - 1)
				: handleStepClick(currentStep - 1);
		}
	};

	const handleTouchStart = (event: TouchEvent<HTMLDivElement>) => {
		startX.current = event.touches[0].clientX;
		startY.current = event.touches[0].clientY;
	};

	const handleTouchMove = (event: TouchEvent<HTMLDivElement>) => {
		const touchY = event.touches[0].clientY;
		const deltaY = touchY - startY.current;

		const touchX = event.touches[0].clientX;
		const deltaX = touchX - startX.current;

		if (Math.abs(deltaX) > Math.abs(deltaY)) {
			event.preventDefault();
			event.stopPropagation();
			direction.current = null;

			if (deltaX < 0) {
				// Scrolling left
				direction.current = 'left';
			} else if (deltaX > 0) {
				// Scrolling right
				direction.current = 'right';
			}
		}
	};

	const changeStep = () => {
		if (!render) {
			return;
		}

		if (direction.current === 'left') {
			if (currentStep < dataLength - 1) {
				setCurrentStep(currentStep + 1);
			}
		} else if (direction.current === 'right') {
			if (currentStep > 0) {
				setCurrentStep(currentStep - 1);
			}
		}
	};

	useEffect(() => {
		if (stepperRef.current && containerRef.current) {
			stepperRef.current.scrollTo({
				left: currentStep * elementWidth,
				behavior: 'smooth',
			});
		}
	}, [currentStep]);

	useEffect(() => {
		if (stepperRef.current) {
			stepperRef.current.addEventListener(
				'touchmove',
				(e) => handleTouchMove(e as unknown as TouchEvent<HTMLDivElement>),
				{ passive: false },
			);
		}

		return () => {
			if (stepperRef.current) {
				stepperRef.current.removeEventListener('touchmove', (e) =>
					handleTouchMove(e as unknown as TouchEvent<HTMLDivElement>),
				);
			}
		};
	}, [stepperRef]);

	return (
		<section className={containerClasses} id={anchorId?.value} ref={containerRef}>
			{currentStep > 0 ? (
				<ArrowIconButton
					className={arrowButtonLeftClasses}
					iconClassName={arrowButtonIconClasses}
					onClick={() => handleButtonClick('down')}
					direction="left"
				/>
			) : null}
			<div
				ref={stepperRef}
				className={wrapperClasses}
				onTouchStart={handleTouchStart}
				onTouchEnd={changeStep}
			>
					{React.Children.toArray(children).map((childrenNode, index) => {
						return (
							<div key={index} className={itemClasses}>
								{childrenNode}
							</div>
						);
					})}
			</div>
			<Stepper
				totalSteps={dataLength}
				currentStep={currentStep}
				onStepClick={handleStepClick}
				stepperRef={stepperRef}
				elementWidth={elementWidth}
			/>
			{currentStep < dataLength - 1 ? (
				<ArrowIconButton
					className={arrowButtonRightClasses}
					iconClassName={arrowButtonIconClasses}
					onClick={() => handleButtonClick('up')}
					direction="right"
				/>
			) : null}
		</section>
	);
};
